import { useState, useEffect, useRef } from 'react';
import { ordersApi, filesApi } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/utils';
import { useTranslation } from '../contexts/TranslationContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { 
    FileText,
    Upload,
    Download,
    CheckCircle,
    AlertTriangle,
    AlertCircle,
    Clock,
    ShieldCheck,
    Package,
    Calendar,
    Mail,
    Truck,
    Bell,
    Lock,
    Send,
    ArrowRight,
    ClipboardCheck,
    ExternalLink,
    Loader2
} from 'lucide-react';

const SUPPLIERS = ["Sony DADC", "GZ Media", "Optimal Media", "MPO", "Other"];

// DSV public track-and-trace URL. ``carrier_tracking_number`` is the
// shippers reference FLOW stores (falls back to the Sony reference —
// see backend `_normalise_carrier_tracking`), so we pass it as
// ``refType=ShippersRefNo`` which is the DSV portal's canonical
// shippers-reference lookup.
const DSV_TRACKING_URL = (ref) =>
    `https://www.dsv.com/mydsv/tracking-public/?refNumber=${encodeURIComponent(ref)}&refType=ShippersRefNo`;

/**
 * DSVTrackingDisplay — admin-only read-only dispatch field that replaces
 * the former "Pallet Number" input. Shows the carrier tracking number
 * sourced from the existing Inbound/DSV tracking integration (persisted
 * on ``supplier_confirmations.dispatch_destinations[].carrier_tracking``)
 * plus a "Track at DSV" button opening the DSV public tracking page in
 * a new tab. Renders "Pending" when tracking has not yet landed.
 *
 * This is NEVER rendered for customers — it lives inside the admin
 * dispatch panel only.
 */
function DSVTrackingDisplay({ carrierTracking, testIdSuffix = '' }) {
    const ref = carrierTracking?.carrier_tracking_number || '';
    const hasRef = !!ref.trim();
    return (
        <div
            className="h-8 px-2 flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 text-sm"
            data-testid={`dsv-tracking-display${testIdSuffix ? `-${testIdSuffix}` : ''}`}
        >
            {hasRef ? (
                <>
                    <span
                        className="font-mono text-xs text-slate-700 truncate"
                        title={ref}
                        data-testid={`dsv-tracking-number${testIdSuffix ? `-${testIdSuffix}` : ''}`}
                    >
                        {ref}
                    </span>
                    <a
                        href={DSV_TRACKING_URL(ref)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 whitespace-nowrap"
                        data-testid={`dsv-tracking-link${testIdSuffix ? `-${testIdSuffix}` : ''}`}
                    >
                        Track at DSV
                        <ExternalLink className="h-3 w-3" />
                    </a>
                </>
            ) : (
                <span
                    className="text-xs italic text-slate-400"
                    data-testid={`dsv-tracking-pending${testIdSuffix ? `-${testIdSuffix}` : ''}`}
                >
                    Pending
                </span>
            )}
        </div>
    );
}

// Workflow Stepper Component - Visual progress indicator
const WorkflowStepper = ({ steps, activeStep, onStepClick }) => {
    return (
        <div className="flex items-center justify-between bg-background rounded-lg p-3 mb-4">
            {steps.map((step, index) => (
                <div key={step.id} className="flex items-center flex-1">
                    {/* Step */}
                    <button
                        onClick={() => !step.disabled && onStepClick(index)}
                        disabled={step.disabled}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                            activeStep === index
                                ? 'bg-white shadow-premium-sm border border-border'
                                : step.disabled
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'hover:bg-white/50 cursor-pointer'
                        }`}
                    >
                        {/* Step Icon */}
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                            step.status === 'complete' 
                                ? 'bg-emerald-100' 
                                : step.status === 'in_progress'
                                    ? 'bg-blue-100'
                                    : 'bg-slate-100'
                        }`}>
                            {step.status === 'complete' ? (
                                <CheckCircle className="h-4 w-4 text-emerald-600" />
                            ) : step.disabled ? (
                                <Lock className="h-3 w-3 text-slate-400" />
                            ) : (
                                <step.icon className={`h-4 w-4 ${
                                    step.status === 'in_progress' ? 'text-blue-600' : 'text-slate-400'
                                }`} />
                            )}
                        </div>
                        
                        {/* Step Label */}
                        <div className="text-left">
                            <p className={`text-xs font-medium ${
                                activeStep === index 
                                    ? 'text-slate-900' 
                                    : step.status === 'complete'
                                        ? 'text-emerald-700'
                                        : step.disabled
                                            ? 'text-slate-400'
                                            : 'text-slate-600'
                            }`}>
                                {step.label}
                            </p>
                            {step.sublabel && (
                                <p className={`text-[10px] ${
                                    step.status === 'complete' ? 'text-emerald-600' : 'text-slate-400'
                                }`}>
                                    {step.sublabel}
                                </p>
                            )}
                        </div>
                    </button>
                    
                    {/* Arrow (except for last step) */}
                    {index < steps.length - 1 && (
                        <div className="flex-1 flex justify-center px-2">
                            <ArrowRight className={`h-4 w-4 ${
                                steps[index].status === 'complete' 
                                    ? 'text-emerald-400' 
                                    : 'text-slate-300'
                            }`} />
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

// Step status indicator (legacy - kept for compatibility)
const StepIndicator = ({ stepNumber, title, status, isActive, isDisabled, onClick }) => {
    const getStatusIcon = () => {
        if (status === 'complete') return <CheckCircle className="h-4 w-4 text-emerald-500" />;
        if (status === 'in_progress') return <Clock className="h-4 w-4 text-amber-500" />;
        if (isDisabled) return <Lock className="h-3 w-3 text-slate-300" />;
        return <span className="text-xs font-medium text-slate-400">{stepNumber}</span>;
    };
    
    return (
        <button
            onClick={onClick}
            disabled={isDisabled}
            className={`flex items-center gap-2 p-2 rounded-lg transition-colors w-full text-left ${
                isActive 
                    ? 'bg-slate-100 border border-slate-300' 
                    : isDisabled 
                        ? 'opacity-50 cursor-not-allowed' 
                        : 'hover:bg-background'
            }`}
        >
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                status === 'complete' ? 'bg-emerald-100' : 
                status === 'in_progress' ? 'bg-amber-100' : 
                'bg-slate-100'
            }`}>
                {getStatusIcon()}
            </div>
            <span className={`text-xs font-medium ${isDisabled ? 'text-slate-400' : 'text-slate-700'}`}>
                {title}
            </span>
        </button>
    );
};

export const SupplierConfirmationSection = ({ order, onUpdate, embedded = false, variant = 'production', isAdmin = false }) => {
    const { t } = useTranslation();
    const [confirmation, setConfirmation] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    // Pre-flight result for "Approve & Send to Sony" — fetched once we
    // know which supplier this order is for. Tells us whether email
    // routing will actually deliver, and surfaces the admin-facing
    // warning if no matching partner record / email_to exists.
    const [partnerPreflight, setPartnerPreflight] = useState(null);
    const [partnerPreflightLoading, setPartnerPreflightLoading] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [activeStep, setActiveStep] = useState(0); // Start with Purchase Order step
    const fileInputRef = useRef(null);
    const dispatchFileInputRef = useRef(null);
    const poFileInputRef = useRef(null);
    
    // PO Lines data for amount mismatch check
    const [poLinesTotal, setPoLinesTotal] = useState(null);
    
    // Step 0: Purchase Order (Sony) form data
    const [poData, setPoData] = useState({
        sony_leos_number: '',
        sony_reference: '',
    });
    
    // Step 1: Confirmation form data
    const [formData, setFormData] = useState({
        supplier: 'Sony DADC',
        sony_order_number: '',
        po_reference: '',
        quantity_confirmed: '',
        unit_price: '',
        total_amount: '',
        currency: 'EUR',
        estimated_ship_date: '',
        estimated_arrival_date: '',
        not_arrive_before_date: '',
    });
    
    // Step 1: Verification checkboxes
    const [verification, setVerification] = useState({
        quantity_verified: false,
        prices_verified: false,
        dates_noted: false,
    });
    
    // Option to attach Invoice Basis PDF to confirmation email
    const [attachInvoiceBasis, setAttachInvoiceBasis] = useState(false);
    
    // Validation errors for approval-required fields
    const [approvalErrors, setApprovalErrors] = useState({});
    // Revision dialog state — admin manually correcting a locked confirmation.
    const [showReviseDialog, setShowReviseDialog] = useState(false);
    const [showHistoryDialog, setShowHistoryDialog] = useState(false);
    const [revising, setRevising] = useState(false);
    const [revisionData, setRevisionData] = useState({
        quantity_confirmed: '',
        unit_price: '',
        total_amount: '',
        currency: '',
        estimated_ship_date: '',
        estimated_arrival_date: '',
        not_arrive_before_date: '',
        po_reference: '',
        reason: '',
    });
    
    // Compute whether approval-required fields are valid
    const qtyValid = formData.quantity_confirmed && Number(formData.quantity_confirmed) > 0;
    const shipDateValid = true; // Est. Ship Date is optional
    const arrivalDateValid = !!formData.estimated_arrival_date;
    const datesLogical = !formData.estimated_ship_date || !arrivalDateValid || formData.estimated_ship_date <= formData.estimated_arrival_date;
    const approvalFieldsReady = qtyValid && arrivalDateValid && datesLogical;
    
    // Step 2: Dispatch form data
    const [dispatchData, setDispatchData] = useState({
        delivery_note_number: '',
        pallet_number: '',
        ship_qty: '',
        planned_arrival_date: '',
        destination: '',
    });
    
    // Step 3: Delivery notice form data (deprecated but kept for data)
    const [deliveryNotice, setDeliveryNotice] = useState({
        message: '',
        delivery_date_override: '',
    });

    useEffect(() => {
        if (order?.id) {
            fetchConfirmation();
            fetchPOLinesTotal();
        }
    }, [order?.id]);

    // Pre-flight resolver for "Approve & Send to Sony". Re-runs whenever
    // the confirmation refreshes — supplier name might have been changed
    // mid-workflow, and a partner record might have been created since
    // the page loaded. Stays silent on errors; only its UI representation
    // (warning banner / disabled button) changes.
    useEffect(() => {
        if (!order?.id) return;
        if (!confirmation || confirmation.po_sent_at) return; // No need once PO already sent
        let cancelled = false;
        (async () => {
            setPartnerPreflightLoading(true);
            try {
                const res = await ordersApi.resolveSupplierPartnerPreflight(order.id, variant);
                if (!cancelled) setPartnerPreflight(res.data);
            } catch (e) {
                if (!cancelled) setPartnerPreflight(null);
            } finally {
                if (!cancelled) setPartnerPreflightLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [order?.id, confirmation?.supplier, confirmation?.po_sent_at]);
    
    // Fetch PO Lines total for amount mismatch check
    const fetchPOLinesTotal = async () => {
        try {
            const res = await ordersApi.getPOLines(order.id);
            if (res.data && res.data.lines) {
                const total = res.data.lines.reduce((sum, line) => sum + (line.line_total || 0), 0);
                setPoLinesTotal(total);
            }
        } catch (error) {
            console.error('Failed to fetch PO lines for amount check:', error);
        }
    };
    
    // Sync Sony LEOS Number to Sony Order # (they represent the same reference)
    useEffect(() => {
        if (poData.sony_leos_number && poData.sony_leos_number !== formData.sony_order_number) {
            setFormData(prev => ({
                ...prev,
                sony_order_number: poData.sony_leos_number
            }));
        }
    }, [poData.sony_leos_number]);

    const fetchConfirmation = async () => {
        try {
            const res = await ordersApi.getSupplierConfirmation(order.id, variant);
            if (res.data) {
                setConfirmation(res.data);
                // Populate Step 0: PO data
                const leosNumber = res.data.sony_leos_number || '';
                setPoData({
                    sony_leos_number: leosNumber,
                    sony_reference: res.data.sony_reference || '',
                });
                // Populate Step 1 form - sony_order_number syncs from LEOS
                setFormData({
                    supplier: res.data.supplier || 'Sony DADC',
                    sony_order_number: leosNumber || res.data.sony_order_number || '',
                    po_reference: res.data.po_reference || '',
                    quantity_confirmed: res.data.quantity_confirmed?.toString() || '',
                    unit_price: res.data.unit_price?.toString() || '',
                    total_amount: res.data.total_amount?.toString() || '',
                    currency: res.data.currency || 'EUR',
                    estimated_ship_date: res.data.estimated_ship_date || '',
                    estimated_arrival_date: res.data.estimated_arrival_date || '',
                    not_arrive_before_date: res.data.not_arrive_before_date || '',
                });
                setVerification({
                    quantity_verified: res.data.quantity_verified || false,
                    prices_verified: res.data.prices_verified || false,
                    dates_noted: res.data.dates_noted || false,
                });
                // Populate Step 2 form
                setDispatchData({
                    delivery_note_number: res.data.dispatch_delivery_note_number || '',
                    pallet_number: res.data.dispatch_pallet_number || '',
                    ship_qty: res.data.dispatch_ship_qty?.toString() || '',
                    planned_arrival_date: res.data.dispatch_planned_arrival_date || '',
                    destination: res.data.dispatch_destination || '',
                });
                // Populate Step 3 form (deprecated)
                setDeliveryNotice({
                    message: res.data.delivery_notice_message || '',
                    delivery_date_override: res.data.delivery_notice_date_override || '',
                });
                
                // Auto-select the correct step based on workflow state
                // Step 0: Purchase Order → Step 1: Confirmation → Step 2: Dispatch
                if (res.data.delivery_notice_sent_at) {
                    // Delivery notice sent - show Dispatch step (completed)
                    setActiveStep(2);
                } else if (res.data.status === 'Approved') {
                    // Confirmation approved - show Dispatch step (next action)
                    setActiveStep(2);
                } else if (res.data.po_sent_at) {
                    // PO sent but not confirmed - show Confirmation step (next action)
                    setActiveStep(1);
                } else {
                    // Still on Purchase Order step
                    setActiveStep(0);
                }
            }
        } catch (error) {
            if (error.response?.status !== 404) {
                console.error('Failed to fetch supplier confirmation');
            }
        } finally {
            setIsLoading(false);
        }
    };
    
    // Fetch BOM/Production Cost Breakdown
    // Step status calculations
    // 3 steps: 1) Purchase Order (Sony) 2) Confirmation 3) Dispatch & Notify
    const isStep0Complete = !!confirmation?.po_sent_at;
    const isStep1Enabled = isStep0Complete; // Confirmation locked until PO is sent
    const isStep1Complete = confirmation?.status === 'Approved';
    const isStep2Enabled = isStep1Complete;

    // Dispatch info: split orders aggregate from dispatch_destinations,
    // single orders use the flat dispatch_delivery_note_number / dispatch_planned_arrival_date.
    const isSplitDelivery = order?.delivery_mode === 'split' && Array.isArray(order?.delivery_addresses) && order.delivery_addresses.length > 0;
    const splitValidation = (() => {
        if (!isSplitDelivery) return { ready: true, problems: [], warnings: [], totalShipped: 0 };
        const addresses = order.delivery_addresses || [];
        const destinations = confirmation?.dispatch_destinations || [];
        const infoByAddr = new Map(destinations.map(d => [d.address_id, d]));
        const orderQty = (order.vinyl_specs?.quantity) || order.quantity || 0;
        const problems = [];
        const warnings = [];
        let totalShipped = 0;
        addresses.forEach((addr, idx) => {
            const info = infoByAddr.get(addr.id) || {};
            const label = addr.label || `Delivery ${idx + 1}`;
            if (!info.delivery_note_number && !info.planned_arrival_date) {
                problems.push(`${label}: missing delivery note number or planned arrival date`);
            }
            if (info.shipped_quantity === undefined || info.shipped_quantity === null || info.shipped_quantity === '') {
                problems.push(`${label}: missing shipped quantity`);
            } else {
                totalShipped += parseInt(info.shipped_quantity, 10) || 0;
            }
        });
        // Over-shipment is allowed (matches legacy single-flow) — show as warning only
        if (orderQty && totalShipped > orderQty) {
            warnings.push(`Total shipped quantity exceeds original order quantity by ${totalShipped - orderQty} units (${totalShipped} / ${orderQty}). Finance will be updated to the actual shipped total.`);
        }
        return { ready: problems.length === 0, problems, warnings, totalShipped, orderQty };
    })();

    const hasDispatchInfo = isSplitDelivery
        ? splitValidation.ready
        : (confirmation?.dispatch_delivery_note_number || confirmation?.dispatch_planned_arrival_date);
    const isDispatchApproved = !!confirmation?.dispatch_approved_at;
    const isStep2Complete = hasDispatchInfo && isDispatchApproved;
    
    const getStep0Status = () => {
        if (isStep0Complete) return 'complete';
        if (confirmation?.sony_leos_number) return 'in_progress';
        if (confirmation) return 'in_progress';
        return 'pending';
    };
    
    const getStep1Status = () => {
        if (isStep1Complete) return 'complete';
        if (isStep1Enabled && confirmation) return 'in_progress';
        return 'pending';
    };
    
    const getStep2Status = () => {
        if (isStep2Complete) return 'complete';
        if (isStep2Enabled) return 'in_progress';
        return 'pending';
    };

    // Step 0 handlers - Purchase Order
    const handleSavePO = async () => {
        setIsSaving(true);
        try {
            await ordersApi.updatePurchaseOrder(order.id, poData, variant);
            toast.success(t('supplier.po_saved', 'Purchase Order saved'));
            await fetchConfirmation();
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('supplier.po_save_failed', 'Failed to save PO'));
        } finally {
            setIsSaving(false);
        }
    };
    
    const handleMarkPOSent = async () => {
        if (!poData.sony_leos_number) {
            toast.error(t('supplier.leos_required', 'Sony LEOS number is required'));
            return;
        }
        
        setIsSaving(true);
        try {
            // Save LEOS first if changed
            await ordersApi.updatePurchaseOrder(order.id, poData, variant);
            // Then mark as sent
            await ordersApi.markPurchaseOrderSent(order.id, variant);
            toast.success(t('supplier.po_sent', 'Purchase Order marked as sent'));
            await fetchConfirmation();
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('supplier.po_send_failed', 'Failed to mark PO as sent'));
        } finally {
            setIsSaving(false);
        }
    };

    // Undo PO Send — admin recovery for an accidental Save & Send. The
    // backend atomically clears the PO lock, OC approval and sidebar
    // TP arrival, and reverts order status (Submitted ← In Progress)
    // or the TP state machine (REQUIRED ← ORDERED). Preconditions
    // enforced server-side: PO is currently marked as sent and no
    // dispatch has been approved yet.
    const [showUndoPODialog, setShowUndoPODialog] = useState(false);
    const [undoPOReason, setUndoPOReason] = useState('');
    const [isUndoingPO, setIsUndoingPO] = useState(false);
    const handleUndoPOSent = async () => {
        setIsUndoingPO(true);
        try {
            await ordersApi.undoPurchaseOrderSent(order.id, variant, {
                reason: undoPOReason.trim() || null,
            });
            toast.success(t(
                'supplier.po_sent_reverted',
                'Purchase Order send reverted — workflow restored to pre-send state'
            ));
            setShowUndoPODialog(false);
            setUndoPOReason('');
            await fetchConfirmation();
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(
                error.response?.data?.detail
                || t('supplier.po_undo_failed', 'Could not revert the PO send — please try again.')
            );
        } finally {
            setIsUndoingPO(false);
        }
    };
    
    const handlePOFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        if (!file.name.toLowerCase().endsWith('.pdf')) {
            toast.error(t('common.pdf_only', 'Only PDF files are allowed'));
            return;
        }
        
        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            await ordersApi.uploadPOPdf(order.id, formData, variant);
            toast.success(t('supplier.pdf_uploaded', 'PO PDF uploaded'));
            await fetchConfirmation();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('common.upload_failed', 'Upload failed'));
        } finally {
            setIsUploading(false);
            if (poFileInputRef.current) poFileInputRef.current.value = '';
        }
    };

    // Step 1 handlers
    const handleSaveConfirmation = async () => {
        setIsSaving(true);
        try {
            const data = {
                ...formData,
                quantity_confirmed: formData.quantity_confirmed ? parseInt(formData.quantity_confirmed) : null,
                unit_price: formData.unit_price ? parseFloat(formData.unit_price) : null,
                total_amount: formData.total_amount ? parseFloat(formData.total_amount) : null,
                ...verification,
            };
            
            let res;
            if (confirmation) {
                res = await ordersApi.updateSupplierConfirmation(order.id, data, variant);
            } else {
                res = await ordersApi.createSupplierConfirmation(order.id, data, variant);
            }
            
            setConfirmation(res.data);
            toast.success(t('supplier.confirmation_saved', 'Confirmation saved'));
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('supplier.save_failed', 'Failed to save'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleApproveConfirmation = async () => {
        // Validate required fields
        const errors = {};
        if (!formData.quantity_confirmed || Number(formData.quantity_confirmed) <= 0) {
            errors.quantity_confirmed = 'Qty Confirmed must be greater than 0';
        }
        if (!formData.estimated_arrival_date) {
            errors.estimated_arrival_date = 'Est. Arrival Date is required';
        }
        if (formData.estimated_ship_date && formData.estimated_arrival_date && formData.estimated_ship_date > formData.estimated_arrival_date) {
            errors.estimated_ship_date = 'Ship date must be on or before arrival date';
        }
        
        if (Object.keys(errors).length > 0) {
            setApprovalErrors(errors);
            // Scroll to first error field
            const firstErrorField = document.querySelector('[data-validation-error="true"]');
            if (firstErrorField) firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            toast.error('Please fill all required fields before approving');
            return;
        }
        setApprovalErrors({});
        
        if (!verification.quantity_verified || !verification.prices_verified || !verification.dates_noted) {
            toast.error(t('supplier.verify_all', 'Please verify all checkboxes before approving'));
            return;
        }
        
        const attachmentNote = attachInvoiceBasis 
            ? '\n\nNote: Invoice Basis PDF will be attached to the customer email.' 
            : '';
        
        if (!window.confirm(t('supplier.approve_confirm', 'Approve this confirmation? This will lock the fields and send email to customer.') + attachmentNote)) {
            return;
        }
        
        setIsApproving(true);
        try {
            const res = await ordersApi.approveSupplierConfirmation(order.id, {
                ...verification,
                attach_invoice_basis: attachInvoiceBasis
            }, variant);
            setConfirmation(res.data);
            toast.success(t('supplier.approved', 'Confirmation approved'));
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('supplier.approve_failed', 'Failed to approve'));
        } finally {
            setIsApproving(false);
        }
    };

    const handleConfirmationPdfUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        if (!file.type.includes('pdf')) {
            toast.error(t('supplier.pdf_only', 'Only PDF files are allowed'));
            return;
        }
        
        if (!confirmation) {
            toast.error(t('supplier.save_first', 'Please save the confirmation details first'));
            return;
        }
        
        setIsUploading(true);
        try {
            await ordersApi.uploadSupplierConfirmationPdf(order.id, file, variant);
            await fetchConfirmation();
            toast.success(t('supplier.pdf_uploaded', 'PDF uploaded'));
        } catch (error) {
            toast.error(error.response?.data?.detail || t('supplier.upload_failed', 'Failed to upload PDF'));
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // Step 2 handlers
    const handleSaveDispatch = async () => {
        setIsSaving(true);
        try {
            const data = {
                delivery_note_number: dispatchData.delivery_note_number || null,
                pallet_number: dispatchData.pallet_number || null,
                ship_qty: dispatchData.ship_qty ? parseInt(dispatchData.ship_qty) : null,
                planned_arrival_date: dispatchData.planned_arrival_date || null,
                destination: dispatchData.destination || null,
            };
            
            const res = await ordersApi.updateDispatch(order.id, data, variant);
            setConfirmation(res.data);
            toast.success(t('supplier.dispatch_draft_saved', 'Dispatch draft saved'));
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('supplier.dispatch_save_failed', 'Failed to save dispatch info'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleApproveDispatch = async () => {
        if (!window.confirm('Approve dispatch and send notification to customer? This action locks dispatch fields.')) {
            return;
        }
        setIsSaving(true);
        try {
            const res = await ordersApi.approveDispatch(order.id, variant);
            setConfirmation(res.data);
            toast.success('Dispatch approved & customer notified');
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to approve dispatch');
        } finally {
            setIsSaving(false);
        }
    };

    // Undo Dispatch — admin recovery for dispatches approved on the wrong
    // order. Preserves all entered data (delivery note, qty, dates, PDF)
    // in ``dispatch_undo_history`` and reopens the Dispatch step for edit.
    // Never touches Purchase Order / Order Confirmation state.
    const [showUndoDispatchDialog, setShowUndoDispatchDialog] = useState(false);
    const [undoReason, setUndoReason] = useState('');
    const [undoAckSent, setUndoAckSent] = useState(false);
    const isDeliveryNoticeSent = !!confirmation?.delivery_notice_sent_at;
    const isProductionVariant = variant === 'production';

    const handleUndoDispatch = async () => {
        // If a customer email was sent the user must tick the ack in the dialog.
        if (isDeliveryNoticeSent && !undoAckSent) {
            toast.error('Please acknowledge that the customer notification cannot be unsent');
            return;
        }
        setIsSaving(true);
        try {
            const res = await ordersApi.undoDispatch(order.id, variant, {
                reason: undoReason.trim() || null,
                acknowledge_notification_sent: undoAckSent,
            });
            const data = res.data || {};
            setConfirmation(data.confirmation || null);
            const restored = data.restored || {};
            const parts = ['Dispatch reopened for editing'];
            if (restored.order_status_before !== restored.order_status_after) {
                parts.push(`order status ${restored.order_status_before} → ${restored.order_status_after}`);
            }
            if (restored.tp_status_reset_to) {
                parts.push(`test press → ${restored.tp_status_reset_to}`);
            }
            const invRevert = restored.invoice_revert;
            if (invRevert?.reverted) {
                parts.push('invoice: Ready to Book → Draft');
            } else if (invRevert?.blocked === 'already_in_economic') {
                // Invoice already left OwnTune for e-conomic; surface a
                // separate warning toast rather than swallowing it in the
                // success message.
                toast.warning(
                    `Invoice #${invRevert.invoice_number} is already in e-conomic (${invRevert.economic_status || 'draft/booked'}). ` +
                    'Sales eligibility was NOT reverted — cancel or credit the invoice in e-conomic if needed.',
                    { duration: 8000 }
                );
            }
            toast.success(parts.join(' · '));
            setShowUndoDispatchDialog(false);
            setUndoReason('');
            setUndoAckSent(false);
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to undo dispatch');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDispatchPdfUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        if (!file.type.includes('pdf')) {
            toast.error(t('supplier.pdf_only', 'Only PDF files are allowed'));
            return;
        }
        
        setIsUploading(true);
        try {
            await ordersApi.uploadDispatchPdf(order.id, file, variant);
            await fetchConfirmation();
            toast.success(t('supplier.dispatch_pdf_uploaded', 'Dispatch PDF uploaded'));
        } catch (error) {
            toast.error(error.response?.data?.detail || t('supplier.upload_failed', 'Failed to upload PDF'));
        } finally {
            setIsUploading(false);
            if (dispatchFileInputRef.current) dispatchFileInputRef.current.value = '';
        }
    };

    // Step 3 handlers
    const handleSendDeliveryNotice = async () => {
        if (!window.confirm(t('supplier.send_delivery_confirm', 'Send delivery notice to customer?'))) {
            return;
        }
        
        setIsSaving(true);
        try {
            const data = {
                message: deliveryNotice.message || null,
                delivery_date_override: deliveryNotice.delivery_date_override || null,
            };
            
            const res = await ordersApi.sendDeliveryNotice(order.id, data, variant);
            setConfirmation(res.data);
            toast.success(t('supplier.delivery_notice_sent', 'Delivery notice sent'));
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('supplier.delivery_notice_failed', 'Failed to send delivery notice'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDownloadPdf = async (fileId, filename) => {
        if (!fileId) return;
        try {
            const result = await filesApi.download(fileId);
            // If it's a local file (blob returned), handle download manually
            if (!result.isS3 && result.data) {
                const url = window.URL.createObjectURL(result.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename || 'document.pdf');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }
            // S3 files are handled by opening in new tab via filesApi.download
        } catch (error) {
            toast.error(t('supplier.download_failed', 'Failed to download PDF'));
        }
    };

    const isConfirmationLocked = confirmation?.status === 'Approved';
    const orderQuantity = order?.vinyl_specs?.quantity;
    // Test Press is intentionally a low-volume workflow — Sony always
    // confirms 5 pcs regardless of the parent production order size.
    // Do NOT surface the mismatch banner for TP.
    const hasQuantityMismatch = variant !== 'test_press' && (
        confirmation?.has_quantity_mismatch ||
        (formData.quantity_confirmed && orderQuantity &&
         parseInt(formData.quantity_confirmed) !== parseInt(orderQuantity))
    );
    
    // BOM/Production Cost for amount comparison - use PO lines total if available
    const bomTotal = poLinesTotal || order?.production_cost_snapshot?.total_cost || 0;
    const bomCurrency = 'EUR'; // PO lines are always in EUR
    const confirmationAmount = formData.total_amount ? parseFloat(formData.total_amount) : null;
    const confirmationCurrency = formData.currency || 'EUR';
    
    // Amount mismatch: compare confirmation total with BOM total (only if same currency)
    const hasAmountMismatch = confirmationAmount && bomTotal > 0 && 
        confirmationCurrency === bomCurrency &&
        Math.abs(confirmationAmount - bomTotal) > 0.01; // Allow small rounding differences

    if (isLoading) {
        if (embedded) {
            return <div className="py-4 text-center text-sm text-slate-400">{t('common.loading', 'Loading...')}</div>;
        }
        return (
            <Card className="border-slate-300 shadow-premium-sm">
                <CardContent className="py-8 text-center text-sm text-slate-400">
                    {t('common.loading', 'Loading...')}
                </CardContent>
            </Card>
        );
    }

    const content = (
        <div data-testid="supplier-confirmation-section">
            {/* Workflow Stepper - Visual progress with arrows */}
            <WorkflowStepper
                steps={[
                    {
                        id: 'po',
                        label: t('supplier.step_po', 'Purchase Order'),
                        sublabel: isStep0Complete ? t('supplier.complete', 'Complete') : null,
                        icon: FileText,
                        status: getStep0Status(),
                        disabled: false,
                    },
                    {
                        id: 'confirmation',
                        label: t('supplier.step_confirmation', 'Order Confirmation'),
                        sublabel: isStep1Complete ? t('supplier.approved', 'Approved') : null,
                        icon: ClipboardCheck,
                        status: getStep1Status(),
                        disabled: !isStep1Enabled,
                    },
                    {
                        id: 'dispatch',
                        label: t('supplier.step_dispatch', 'Dispatch'),
                        sublabel: isStep2Complete ? t('supplier.delivered', 'Delivered') : null,
                        icon: Truck,
                        status: getStep2Status(),
                        disabled: !isStep2Enabled,
                    },
                ]}
                activeStep={activeStep}
                onStepClick={setActiveStep}
            />

            {/* Step 0: Purchase Order (Sony) */}
            {activeStep === 0 && (
                <div className="space-y-4">
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                        <FileText className="h-4 w-4" />
                        <span>{t('supplier.po_info', 'Register Sony purchase order details')}</span>
                    </div>
                    
                    {/* PO Sent completion banner */}
                    {isStep0Complete && (
                        <div className="flex items-start justify-between gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-700">
                            <div className="flex items-start gap-2">
                                <CheckCircle className="h-4 w-4 shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-medium">{t('supplier.po_sent_complete', 'Purchase Order sent to Sony')}</p>
                                    <p className="text-emerald-600">
                                        {formatDateTime(confirmation.po_sent_at)} {t('common.by', 'by')} {confirmation.po_sent_by_name}
                                    </p>
                                </div>
                            </div>
                            {/* Admin recovery — reverse an accidental Save & Send.
                                Hidden once dispatch is approved (backend also enforces this). */}
                            {!confirmation?.dispatch_approved_at && (
                                <button
                                    type="button"
                                    onClick={() => { setUndoPOReason(''); setShowUndoPODialog(true); }}
                                    className="shrink-0 text-[11px] font-medium text-slate-500 hover:text-amber-700 hover:underline"
                                    data-testid="po-sent-undo-btn"
                                    title={t('supplier.po_undo_hint', 'Revert this PO send — restores the workflow to the pre-send state')}
                                >
                                    {t('supplier.po_undo', 'Revert send')}
                                </button>
                            )}
                        </div>
                    )}
                    
                    {/* Internal PO Number (read-only) */}
                    <div className="space-y-1">
                        <Label className="text-xs font-medium">{t('supplier.internal_po', 'Internal PO Number')}</Label>
                        <div className="flex items-center gap-2">
                            <Input
                                value={confirmation?.internal_po_number || '—'}
                                className="h-8 text-sm bg-background font-mono"
                                disabled
                                data-testid="internal-po-number"
                            />
                            <span className="text-[10px] text-slate-400">{t('common.auto_generated', 'Auto-generated')}</span>
                        </div>
                    </div>
                    
                    {/* Sony LEOS Number (mandatory) */}
                    <div className="space-y-1">
                        <Label className="text-xs font-medium">
                            {t('supplier.leos_number', 'Sony LEOS Number')} 
                            <span className="text-red-500 ml-1">*</span>
                        </Label>
                        <Input
                            value={poData.sony_leos_number}
                            onChange={(e) => setPoData({...poData, sony_leos_number: e.target.value})}
                            className="h-8 text-sm"
                            placeholder="e.g., LEOS-123456"
                            disabled={isStep0Complete}
                            data-testid="leos-number-input"
                        />
                        <p className="text-[10px] text-slate-400">
                            {t('supplier.leos_hint', 'Sony internal order reference - required before confirmation')}
                        </p>
                    </div>
                    
                    {/* Sony Reference (optional) */}
                    <div className="space-y-1">
                        <Label className="text-xs">{t('supplier.sony_reference', 'Sony Reference')} ({t('common.optional', 'Optional')})</Label>
                        <Input
                            value={poData.sony_reference}
                            onChange={(e) => setPoData({...poData, sony_reference: e.target.value})}
                            className="h-8 text-sm"
                            placeholder="Additional Sony reference"
                            disabled={isStep0Complete}
                            data-testid="sony-reference-input"
                        />
                    </div>
                    
                    {/* PO PDF Upload (optional) */}
                    <div className="space-y-1">
                        <Label className="text-xs">{t('supplier.po_pdf', 'PO Document')} ({t('common.optional', 'Optional')})</Label>
                        {confirmation?.po_pdf_filename ? (
                            <div className="flex items-center justify-between p-2 bg-background rounded border">
                                <div className="flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-slate-400" />
                                    <span className="text-sm">{confirmation.po_pdf_filename}</span>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDownloadPdf(confirmation.po_pdf_file_id, confirmation.po_pdf_filename)}
                                    className="h-7 text-xs"
                                >
                                    <Download className="h-3 w-3 mr-1" />
                                    {t('common.download', 'Download')}
                                </Button>
                            </div>
                        ) : !isStep0Complete && (
                            <div className="flex items-center gap-2">
                                <input
                                    type="file"
                                    accept=".pdf"
                                    ref={poFileInputRef}
                                    onChange={handlePOFileUpload}
                                    className="hidden"
                                />
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => poFileInputRef.current?.click()}
                                    disabled={isUploading}
                                    className="h-8 text-xs"
                                >
                                    <Upload className="h-3 w-3 mr-1" />
                                    {isUploading ? t('common.uploading', 'Uploading...') : t('supplier.upload_po_pdf', 'Upload PO PDF')}
                                </Button>
                            </div>
                        )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col gap-2 pt-3 border-t">
                        {/* Pre-flight partner-resolution warning. Surfaces
                            the recipient mismatch BEFORE the admin clicks
                            the action — solves the silent-fail case where
                            confirmation.supplier didn't match a partner. */}
                        {!isStep0Complete && partnerPreflight && !partnerPreflight.ok && (
                            <div
                                className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700"
                                data-testid="supplier-preflight-warning"
                            >
                                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                    <strong className="block">No email recipient found</strong>
                                    <span>{partnerPreflight.warning}</span>
                                </div>
                            </div>
                        )}
                        {!isStep0Complete && partnerPreflight && partnerPreflight.ok && (
                            <div
                                className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-700"
                                data-testid="supplier-preflight-ok"
                                title={`Matched via: ${partnerPreflight.partner?.match_reason}`}
                            >
                                <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
                                <span className="truncate">
                                    Email will go to {partnerPreflight.partner?.name}: {partnerPreflight.partner?.email_to?.join(', ')}
                                </span>
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                        {!isStep0Complete ? (
                            <>
                                <Button 
                                    variant="outline"
                                    size="sm"
                                    onClick={handleSavePO}
                                    disabled={isSaving}
                                    className="h-8 text-xs"
                                >
                                    {isSaving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                                </Button>
                                <Button 
                                    size="sm"
                                    onClick={handleMarkPOSent}
                                    disabled={isSaving || !poData.sony_leos_number || (partnerPreflight && !partnerPreflight.ok)}
                                    className="h-8 text-xs bg-blue-600 hover:bg-blue-700"
                                    title={
                                        partnerPreflight && !partnerPreflight.ok
                                            ? 'Resolve the recipient warning above before sending'
                                            : undefined
                                    }
                                    data-testid="supplier-approve-send-btn"
                                >
                                    <Send className="h-3 w-3 mr-1" />
                                    {isSaving ? t('common.processing', 'Processing...') : 'Approve & Send to Sony'}
                                </Button>
                            </>
                        ) : (
                            <span className="text-xs text-emerald-600 flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" />
                                {t('supplier.po_complete', 'PO registered and sent')}
                            </span>
                        )}
                        </div>
                    </div>
                    
                    {/* Lock notice */}
                    {!isStep0Complete && !poData.sony_leos_number && (
                        <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                            <Lock className="h-4 w-4" />
                            <span>{t('supplier.confirmation_locked', 'Confirmation step is locked until PO is sent with LEOS number')}</span>
                        </div>
                    )}
                </div>
            )}

            {/* Step 1: Confirmation */}
            {activeStep === 1 && (
                <div className="space-y-4">
                    {/* Lock notice if PO not sent */}
                    {!isStep1Enabled && (
                        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                            <Lock className="h-4 w-4" />
                            <span>{t('supplier.complete_po_first', 'Complete Purchase Order step first (LEOS number required)')}</span>
                        </div>
                    )}

                    {/* Inbound-Hub driven verification banner — appears
                        whenever the ``needs_verification`` flag is set on
                        the confirmation row (see supplier.py + inbound.py).
                        Cleared automatically by Approve & Send.  */}
                    {confirmation?.needs_verification && (
                        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800"
                             data-testid="sc-needs-verification-banner">
                            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <div className="font-medium mb-0.5">
                                    Supplier confirmation needs verification
                                </div>
                                <div>
                                    Inbound Hub updated
                                    {Array.isArray(confirmation.needs_verification_changed_fields) && confirmation.needs_verification_changed_fields.length > 0 ? (
                                        <> the following field{confirmation.needs_verification_changed_fields.length !== 1 ? 's' : ''}: {' '}
                                            <span className="font-semibold">
                                                {confirmation.needs_verification_changed_fields.join(', ')}
                                            </span>
                                        </>
                                    ) : (
                                        <> one or more admin-verified fields</>
                                    )}
                                    . Review the values below and click <em>Approve &amp; Send</em> to clear this flag.
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Mismatch Warnings */}
                    {(hasQuantityMismatch || hasAmountMismatch) && (
                        <div className="space-y-2">
                            {hasQuantityMismatch && (
                                <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                                    <span>
                                        {t('supplier.qty_mismatch', 'Quantity mismatch')}: {t('supplier.order_has', 'Order has')} {orderQuantity?.toLocaleString()}, {t('supplier.confirmation_has', 'confirmation has')} {formData.quantity_confirmed ? parseInt(formData.quantity_confirmed).toLocaleString() : '—'}
                                    </span>
                                </div>
                            )}
                            {hasAmountMismatch && (
                                <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                                    <span>
                                        {t('supplier.amount_mismatch', 'Amount mismatch')}: {t('supplier.bom_total_is', 'BOM total is')} {bomTotal?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} {bomCurrency}, {t('supplier.confirmation_total_is', 'confirmation total is')} {confirmationAmount?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} {confirmationCurrency}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Order Details */}
                    <div>
                        <h4 className="text-[10px] font-medium uppercase tracking-wider text-slate-500 mb-2">{t('supplier.order_details', 'Order Details')}</h4>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1">
                                <Label className="text-xs">{t('supplier.supplier', 'Supplier')}</Label>
                                <Select 
                                    value={formData.supplier} 
                                    onValueChange={(v) => setFormData({...formData, supplier: v})}
                                    disabled={isConfirmationLocked}
                                >
                                    <SelectTrigger className="h-8 text-sm" data-testid="supplier-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SUPPLIERS.map((s) => (
                                            <SelectItem key={s} value={s}>{s}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">
                                    {t('supplier.sony_order', 'Sony Order #')}
                                    <span className="text-[10px] text-slate-400 ml-1">(from LEOS)</span>
                                </Label>
                                <Input
                                    value={formData.sony_order_number}
                                    className="h-8 text-sm bg-slate-50"
                                    disabled={true}
                                    data-testid="sony-order-number-input"
                                    title="Auto-populated from Sony LEOS Number"
                                />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">{t('supplier.po_reference', 'PO Reference')}</Label>
                                <Input
                                    value={formData.po_reference}
                                    onChange={(e) => setFormData({...formData, po_reference: e.target.value})}
                                    className="h-8 text-sm"
                                    disabled={isConfirmationLocked}
                                    data-testid="po-reference-input"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Quantity & Pricing */}
                    <div>
                        <h4 className="text-[10px] font-medium uppercase tracking-wider text-slate-500 mb-2">{t('supplier.qty_pricing', 'Quantity & Pricing')}</h4>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1">
                                <Label className="text-xs">
                                    {t('supplier.qty_confirmed', 'Qty Confirmed')} <span className="text-red-500">*</span>
                                    {orderQuantity && <span className="text-slate-400 ml-1">({t('supplier.order', 'order')}: {orderQuantity?.toLocaleString()})</span>}
                                </Label>
                                <Input
                                    type="number"
                                    value={formData.quantity_confirmed}
                                    onChange={(e) => { setFormData({...formData, quantity_confirmed: e.target.value}); setApprovalErrors(prev => ({...prev, quantity_confirmed: undefined})); }}
                                    className={`h-8 text-sm ${approvalErrors.quantity_confirmed ? 'border-red-400 bg-red-50' : hasQuantityMismatch ? 'border-red-300 bg-red-50' : ''}`}
                                    disabled={isConfirmationLocked}
                                    data-testid="quantity-confirmed-input"
                                    data-validation-error={approvalErrors.quantity_confirmed ? "true" : undefined}
                                />
                                {approvalErrors.quantity_confirmed && <p className="text-[10px] text-red-500 mt-0.5">{approvalErrors.quantity_confirmed}</p>}
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">
                                    {t('supplier.total_amount', 'Total Amount')}
                                    {bomTotal > 0 && <span className="text-slate-400 ml-1">({t('supplier.bom', 'BOM')}: {bomTotal?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} {bomCurrency})</span>}
                                </Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={formData.total_amount}
                                    onChange={(e) => setFormData({...formData, total_amount: e.target.value})}
                                    className={`h-8 text-sm ${hasAmountMismatch ? 'border-amber-300 bg-amber-50' : ''}`}
                                    disabled={isConfirmationLocked}
                                    data-testid="total-amount-input"
                                />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">{t('supplier.currency', 'Currency')}</Label>
                                <Select 
                                    value={formData.currency} 
                                    onValueChange={(v) => setFormData({...formData, currency: v})}
                                    disabled={isConfirmationLocked}
                                >
                                    <SelectTrigger className="h-8 text-sm">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="EUR">EUR</SelectItem>
                                        <SelectItem value="USD">USD</SelectItem>
                                        <SelectItem value="DKK">DKK</SelectItem>
                                        <SelectItem value="GBP">GBP</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    {/* Dates */}
                    <div>
                        <h4 className="text-[10px] font-medium uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {t('supplier.dates', 'Dates')}
                        </h4>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1">
                                <Label className="text-xs">{t('supplier.est_ship_date', 'Est. Ship Date')}</Label>
                                <Input
                                    type="date"
                                    value={formData.estimated_ship_date}
                                    onChange={(e) => { setFormData({...formData, estimated_ship_date: e.target.value}); setApprovalErrors(prev => ({...prev, estimated_ship_date: undefined})); }}
                                    className={`h-8 text-sm ${approvalErrors.estimated_ship_date ? 'border-red-400 bg-red-50' : ''}`}
                                    disabled={isConfirmationLocked}
                                    data-testid="est-ship-date-input"
                                    data-validation-error={approvalErrors.estimated_ship_date ? "true" : undefined}
                                />
                                {approvalErrors.estimated_ship_date && <p className="text-[10px] text-red-500 mt-0.5">{approvalErrors.estimated_ship_date}</p>}
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">{t('supplier.est_arrival_date', 'Est. Arrival Date')} <span className="text-red-500">*</span></Label>
                                <Input
                                    type="date"
                                    value={formData.estimated_arrival_date}
                                    onChange={(e) => { setFormData({...formData, estimated_arrival_date: e.target.value}); setApprovalErrors(prev => ({...prev, estimated_arrival_date: undefined})); }}
                                    className={`h-8 text-sm ${approvalErrors.estimated_arrival_date ? 'border-red-400 bg-red-50' : ''}`}
                                    disabled={isConfirmationLocked}
                                    data-testid="est-arrival-date-input"
                                    data-validation-error={approvalErrors.estimated_arrival_date ? "true" : undefined}
                                />
                                {approvalErrors.estimated_arrival_date && <p className="text-[10px] text-red-500 mt-0.5">{approvalErrors.estimated_arrival_date}</p>}
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">{t('supplier.not_before', 'Not Arrive Before')}</Label>
                                <Input
                                    type="date"
                                    value={formData.not_arrive_before_date}
                                    onChange={(e) => setFormData({...formData, not_arrive_before_date: e.target.value})}
                                    className="h-8 text-sm"
                                    disabled={isConfirmationLocked}
                                />
                            </div>
                        </div>
                    </div>

                    {/* PDF Upload */}
                    <div>
                        <h4 className="text-[10px] font-medium uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                            <FileText className="h-3 w-3" />
                            {t('supplier.confirmation_pdf', 'Confirmation PDF')}
                        </h4>
                        {confirmation?.pdf_filename ? (
                            <div className="flex items-center justify-between p-2 bg-white border border-border rounded">
                                <div className="flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-red-500" />
                                    <div>
                                        <p className="text-xs font-medium">{confirmation.pdf_filename}</p>
                                        <p className="text-[10px] text-slate-400">
                                            {t('supplier.uploaded', 'Uploaded')} {formatDateTime(confirmation.pdf_uploaded_at)}
                                        </p>
                                    </div>
                                </div>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-7" 
                                    onClick={() => handleDownloadPdf(confirmation.pdf_file_id, confirmation.pdf_filename)}
                                >
                                    <Download className="h-3 w-3 mr-1" />
                                    {t('common.download', 'Download')}
                                </Button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".pdf,application/pdf"
                                    onChange={handleConfirmationPdfUpload}
                                    className="hidden"
                                    disabled={!confirmation || isConfirmationLocked}
                                />
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 text-xs"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={!confirmation || isUploading || isConfirmationLocked}
                                >
                                    <Upload className="h-3 w-3 mr-1" />
                                    {isUploading ? t('common.uploading', 'Uploading...') : t('supplier.upload_pdf', 'Upload PDF')}
                                </Button>
                                {!confirmation && (
                                    <span className="text-[10px] text-slate-400">{t('supplier.save_first', 'Save confirmation first')}</span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Verification Checkboxes */}
                    <div className="pt-2 border-t">
                        <h4 className="text-[10px] font-medium uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                            <ShieldCheck className="h-3 w-3" />
                            {t('supplier.verification', 'Verification')}
                        </h4>
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 text-xs cursor-pointer">
                                <Checkbox
                                    checked={verification.quantity_verified}
                                    onCheckedChange={(checked) => setVerification({...verification, quantity_verified: checked})}
                                    disabled={isConfirmationLocked}
                                />
                                <span className={verification.quantity_verified ? 'text-emerald-600' : ''}>
                                    {t('supplier.qty_matches', 'Quantity matches order')}
                                </span>
                            </label>
                            <label className="flex items-center gap-2 text-xs cursor-pointer">
                                <Checkbox
                                    checked={verification.prices_verified}
                                    onCheckedChange={(checked) => setVerification({...verification, prices_verified: checked})}
                                    disabled={isConfirmationLocked}
                                />
                                <span className={verification.prices_verified ? 'text-emerald-600' : ''}>
                                    {t('supplier.prices_verified', 'Prices verified')}
                                </span>
                            </label>
                            <label className="flex items-center gap-2 text-xs cursor-pointer">
                                <Checkbox
                                    checked={verification.dates_noted}
                                    onCheckedChange={(checked) => setVerification({...verification, dates_noted: checked})}
                                    disabled={isConfirmationLocked}
                                />
                                <span className={verification.dates_noted ? 'text-emerald-600' : ''}>
                                    {t('supplier.dates_noted', 'Dates noted')}
                                </span>
                            </label>
                        </div>
                    </div>
                    
                    {/* Attach Invoice Basis Option */}
                    {confirmation && !isConfirmationLocked && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <label className="flex items-center gap-2 text-xs cursor-pointer">
                                <Checkbox
                                    checked={attachInvoiceBasis}
                                    onCheckedChange={(checked) => setAttachInvoiceBasis(checked)}
                                />
                                <div className="flex flex-col">
                                    <span className={`font-medium ${attachInvoiceBasis ? 'text-blue-700' : 'text-slate-700'}`}>
                                        {t('supplier.attach_invoice_basis', 'Attach Invoice Basis (PDF)')}
                                    </span>
                                    <span className="text-[10px] text-slate-500">
                                        {t('supplier.attach_invoice_basis_desc', 'Includes detailed price breakdown in customer email')}
                                    </span>
                                </div>
                            </label>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-3 border-t">
                        <Button 
                            variant="outline"
                            size="sm"
                            onClick={handleSaveConfirmation}
                            disabled={isSaving || isConfirmationLocked}
                            className="h-8 text-xs"
                        >
                            {isSaving ? t('common.saving', 'Saving...') : 'Save'}
                        </Button>
                        
                        {confirmation && !isConfirmationLocked && (
                            <Button
                                size="sm"
                                onClick={handleApproveConfirmation}
                                disabled={isApproving || !approvalFieldsReady || !verification.quantity_verified || !verification.prices_verified || !verification.dates_noted}
                                className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
                                data-testid="approve-send-btn"
                            >
                                <CheckCircle className="h-3 w-3 mr-1" />
                                {isApproving ? t('common.approving', 'Approving...') : 'Approve & Send to customer'}
                            </Button>
                        )}
                        
                        {isConfirmationLocked && (
                            <div className="flex items-center gap-3 flex-wrap">
                                <span className="text-[10px] text-emerald-600 flex items-center gap-1">
                                    <CheckCircle className="h-3 w-3" />
                                    {t('supplier.locked', 'Locked (Approved)')}
                                    {confirmation?.current_version > 1 && (
                                        <span className="ml-1 px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 font-medium">
                                            V{confirmation.current_version}
                                        </span>
                                    )}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setShowReviseDialog(true)}
                                    className="text-[11px] text-blue-700 hover:text-blue-900 underline"
                                    data-testid="revise-confirmation-btn"
                                >
                                    Revise confirmation
                                </button>
                                {(confirmation?.versions?.length || 0) > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setShowHistoryDialog(true)}
                                        className="text-[11px] text-slate-600 hover:text-slate-900 underline"
                                        data-testid="view-confirmation-history-btn"
                                    >
                                        View history ({(confirmation.versions?.length || 0) + 1} versions)
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Step 2: Dispatch & Notify (merged) */}
            {activeStep === 2 && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                        <div className="flex items-center gap-2">
                            <Truck className="h-4 w-4" />
                            <span>{t('supplier.dispatch_info', 'Dispatch & delivery note information')}</span>
                        </div>
                        <div className="flex items-center gap-1 text-slate-400">
                            <span>{isDispatchApproved ? 'Locked — approved & sent' : 'Review and approve before sending'}</span>
                        </div>
                    </div>

                    {/* Show completion status if already dispatched */}
                    {isStep2Complete && (
                        <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-700 mb-2">
                            <CheckCircle className="h-4 w-4" />
                            <div>
                                <p className="font-medium">{t('supplier.dispatch_complete', 'Dispatched & Customer Notified')}</p>
                                <p className="text-emerald-600">
                                    {formatDateTime(confirmation.delivery_notice_sent_at)} {t('common.by', 'by')} {confirmation.delivery_notice_sent_by_name || confirmation.dispatch_updated_by_name}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Split Delivery: per-destination dispatch */}
                    {order?.delivery_mode === 'split' && order?.delivery_addresses?.length > 0 ? (
                        <SplitDispatchDestinations
                            order={order}
                            confirmation={confirmation}
                            isDispatchApproved={isDispatchApproved}
                            onUpdated={(updated) => {
                                setConfirmation(updated);
                                if (onUpdate) onUpdate();
                            }}
                            variant={variant}
                        />
                    ) : (
                    <>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs">{t('supplier.delivery_note_number', 'Delivery Note Number')}</Label>
                            <Input
                                value={dispatchData.delivery_note_number}
                                onChange={(e) => setDispatchData({...dispatchData, delivery_note_number: e.target.value})}
                                className="h-8 text-sm"
                                placeholder="e.g., DN-2026-001234"
                                data-testid="delivery-note-number-input"
                                disabled={isDispatchApproved}
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">{t('supplier.dsv_tracking', 'DSV Tracking')}</Label>
                            <DSVTrackingDisplay
                                carrierTracking={(confirmation?.dispatch_destinations || [])[0]?.carrier_tracking}
                                testIdSuffix="single"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs">{t('supplier.ship_qty', 'Ship Quantity')}</Label>
                            <Input
                                type="number"
                                value={dispatchData.ship_qty}
                                onChange={(e) => setDispatchData({...dispatchData, ship_qty: e.target.value})}
                                className="h-8 text-sm"
                                data-testid="ship-qty-input"
                                disabled={isDispatchApproved}
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">{t('supplier.planned_arrival', 'Planned Arrival Date')}</Label>
                            <Input
                                type="date"
                                value={dispatchData.planned_arrival_date}
                                onChange={(e) => setDispatchData({...dispatchData, planned_arrival_date: e.target.value})}
                                className="h-8 text-sm"
                                data-testid="planned-arrival-input"
                                disabled={isDispatchApproved}
                            />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <Label className="text-xs">{t('supplier.destination', 'Destination')}</Label>
                        <Textarea
                            value={dispatchData.destination}
                            onChange={(e) => setDispatchData({...dispatchData, destination: e.target.value})}
                            className="text-sm min-h-[80px] resize-y"
                            rows={3}
                            placeholder={t('supplier.destination_placeholder', 'Warehouse address or delivery location (multi-line supported)')}
                            data-testid="destination-input"
                            disabled={isDispatchApproved}
                        />
                    </div>
                    </>
                    )}

                    {/* Dispatch PDF Upload */}
                    <div>
                        <h4 className="text-[10px] font-medium uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                            <FileText className="h-3 w-3" />
                            {t('supplier.dispatch_pdf', 'Dispatch/Delivery Note PDF')}
                        </h4>
                        {confirmation?.dispatch_pdf_filename ? (
                            <div className="flex items-center justify-between p-2 bg-white border border-border rounded">
                                <div className="flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-red-500" />
                                    <div>
                                        <p className="text-xs font-medium">{confirmation.dispatch_pdf_filename}</p>
                                        <p className="text-[10px] text-slate-400">
                                            {t('supplier.uploaded', 'Uploaded')} {formatDateTime(confirmation.dispatch_updated_at)}
                                        </p>
                                    </div>
                                </div>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-7" 
                                    onClick={() => handleDownloadPdf(confirmation.dispatch_pdf_file_id, confirmation.dispatch_pdf_filename)}
                                >
                                    <Download className="h-3 w-3 mr-1" />
                                    {t('common.download', 'Download')}
                                </Button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <input
                                    ref={dispatchFileInputRef}
                                    type="file"
                                    accept=".pdf,application/pdf"
                                    onChange={handleDispatchPdfUpload}
                                    className="hidden"
                                />
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 text-xs"
                                    onClick={() => dispatchFileInputRef.current?.click()}
                                    disabled={isUploading}
                                >
                                    <Upload className="h-3 w-3 mr-1" />
                                    {isUploading ? t('common.uploading', 'Uploading...') : t('supplier.upload_pdf', 'Upload PDF')}
                                </Button>
                                <span className="text-[10px] text-slate-400">{t('common.optional', 'Optional')}</span>
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="space-y-2 pt-3 border-t">
                        {/* Validation summary — shown when dispatch is incomplete (split mode) */}
                        {!isDispatchApproved && isSplitDelivery && !splitValidation.ready && (
                            <div className="p-2.5 rounded border border-amber-200 bg-amber-50 text-xs text-amber-800" data-testid="dispatch-validation-summary">
                                <div className="font-semibold mb-1 flex items-center gap-1">
                                    <AlertTriangle className="h-3.5 w-3.5" />
                                    Approval blocked — fix the following:
                                </div>
                                <ul className="ml-5 list-disc space-y-0.5">
                                    {splitValidation.problems.map((p, i) => (
                                        <li key={i}>{p}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {/* Over-shipment warning (non-blocking) */}
                        {!isDispatchApproved && isSplitDelivery && splitValidation.warnings.length > 0 && (
                            <div className="p-2.5 rounded border border-orange-200 bg-orange-50 text-xs text-orange-800" data-testid="dispatch-overshipment-warning">
                                <div className="font-semibold mb-1 flex items-center gap-1">
                                    <AlertTriangle className="h-3.5 w-3.5" />
                                    Warning
                                </div>
                                <ul className="ml-5 list-disc space-y-0.5">
                                    {splitValidation.warnings.map((w, i) => (
                                        <li key={i}>{w}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {!isDispatchApproved && isSplitDelivery && splitValidation.ready && splitValidation.warnings.length === 0 && splitValidation.orderQty && (
                            <div className="p-2 rounded border border-emerald-200 bg-emerald-50 text-xs text-emerald-700" data-testid="dispatch-validation-ok">
                                <CheckCircle className="h-3.5 w-3.5 inline mr-1" />
                                All {order.delivery_addresses.length} destinations ready. Total shipped: {splitValidation.totalShipped} / {splitValidation.orderQty} units.
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                        {!isDispatchApproved ? (
                            <>
                                {!isSplitDelivery && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleSaveDispatch}
                                        disabled={isSaving || (!dispatchData.delivery_note_number && !dispatchData.planned_arrival_date && !dispatchData.ship_qty)}
                                        className="h-8 text-xs"
                                        data-testid="dispatch-save-btn"
                                    >
                                        {isSaving ? t('common.saving', 'Saving...') : 'Save'}
                                    </Button>
                                )}
                                <Button
                                    size="sm"
                                    onClick={handleApproveDispatch}
                                    disabled={isSaving || !hasDispatchInfo}
                                    className="h-8 text-xs bg-blue-600 hover:bg-blue-700"
                                    data-testid="dispatch-approve-send-btn"
                                    title={!hasDispatchInfo ? (isSplitDelivery ? 'Fill all destinations before approving' : 'Save dispatch data first') : ''}
                                >
                                    <Truck className="h-3 w-3 mr-1" />
                                    {isSaving ? t('common.saving', 'Saving...') : 'Approve & Send dispatch notification'}
                                </Button>
                            </>
                        ) : (
                            <div className="flex items-center gap-3 text-xs">
                                <div className="flex items-center gap-2 text-emerald-600">
                                    <CheckCircle className="h-3.5 w-3.5" />
                                    <span className="font-medium">Dispatch approved & sent</span>
                                    <span className="text-slate-400">
                                        {formatDateTime(confirmation.dispatch_approved_at)} by {confirmation.dispatch_approved_by_name}
                                    </span>
                                </div>
                                {isAdmin && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                            setUndoReason('');
                                            setUndoAckSent(false);
                                            setShowUndoDispatchDialog(true);
                                        }}
                                        className="h-7 text-[11px] border-amber-300 text-amber-700 hover:bg-amber-50"
                                        data-testid="dispatch-undo-btn"
                                        title="Reopen the Dispatch step for editing. Preserves all entered data. Does not touch PO/OC."
                                    >
                                        <AlertTriangle className="h-3 w-3 mr-1" />
                                        Undo dispatch
                                    </Button>
                                )}
                            </div>
                        )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    const dialogs = (
        <>
            {/* Revise Confirmation dialog — admin manually correcting a
                locked/approved supplier confirmation. Creates V2, V3, …
                Every submission requires a reason for the audit log. */}
            <Dialog open={showReviseDialog} onOpenChange={setShowReviseDialog}>
                <DialogContent className="sm:max-w-2xl" data-testid="revise-confirmation-dialog">
                    <DialogHeader>
                        <DialogTitle>Revise supplier confirmation — V{(confirmation?.current_version || 1) + 1}</DialogTitle>
                        <DialogDescription>
                            Correct quantities, prices, dates or PDF for an already-approved confirmation without unlocking the original.
                            The previous version (V{confirmation?.current_version || 1}) is preserved in the audit history.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid grid-cols-2 gap-3 py-2">
                        <div>
                            <Label className="text-xs">Quantity confirmed</Label>
                            <Input
                                type="number"
                                value={revisionData.quantity_confirmed}
                                onChange={(e) => setRevisionData({ ...revisionData, quantity_confirmed: e.target.value })}
                                placeholder={confirmation?.quantity_confirmed?.toString() || ''}
                                data-testid="revise-quantity"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Total amount</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={revisionData.total_amount}
                                onChange={(e) => setRevisionData({ ...revisionData, total_amount: e.target.value })}
                                placeholder={confirmation?.total_amount?.toString() || ''}
                                data-testid="revise-total"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Unit price</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={revisionData.unit_price}
                                onChange={(e) => setRevisionData({ ...revisionData, unit_price: e.target.value })}
                                placeholder={confirmation?.unit_price?.toString() || ''}
                                data-testid="revise-unit-price"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Currency</Label>
                            <Input
                                value={revisionData.currency}
                                onChange={(e) => setRevisionData({ ...revisionData, currency: e.target.value.toUpperCase() })}
                                placeholder={confirmation?.currency || 'EUR'}
                                maxLength={3}
                                data-testid="revise-currency"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Estimated ship date</Label>
                            <Input
                                type="date"
                                value={revisionData.estimated_ship_date}
                                onChange={(e) => setRevisionData({ ...revisionData, estimated_ship_date: e.target.value })}
                                data-testid="revise-ship-date"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Estimated arrival date</Label>
                            <Input
                                type="date"
                                value={revisionData.estimated_arrival_date}
                                onChange={(e) => setRevisionData({ ...revisionData, estimated_arrival_date: e.target.value })}
                                data-testid="revise-arrival-date"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Not arrive before</Label>
                            <Input
                                type="date"
                                value={revisionData.not_arrive_before_date}
                                onChange={(e) => setRevisionData({ ...revisionData, not_arrive_before_date: e.target.value })}
                                data-testid="revise-not-before"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">PO reference</Label>
                            <Input
                                value={revisionData.po_reference}
                                onChange={(e) => setRevisionData({ ...revisionData, po_reference: e.target.value })}
                                placeholder={confirmation?.po_reference || ''}
                                data-testid="revise-po"
                            />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">Reason for revision *</Label>
                            <Textarea
                                value={revisionData.reason}
                                onChange={(e) => setRevisionData({ ...revisionData, reason: e.target.value })}
                                placeholder="e.g. Supplier sent corrected confirmation with different ship date"
                                rows={2}
                                required
                                data-testid="revise-reason"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" size="sm" onClick={() => setShowReviseDialog(false)} disabled={revising}>
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            disabled={revising || !revisionData.reason?.trim()}
                            onClick={async () => {
                                const payload = { reason: revisionData.reason.trim() };
                                if (revisionData.quantity_confirmed) payload.quantity_confirmed = parseInt(revisionData.quantity_confirmed, 10);
                                if (revisionData.total_amount) payload.total_amount = parseFloat(revisionData.total_amount);
                                if (revisionData.unit_price) payload.unit_price = parseFloat(revisionData.unit_price);
                                if (revisionData.currency) payload.currency = revisionData.currency.trim().toUpperCase();
                                ['estimated_ship_date', 'estimated_arrival_date', 'not_arrive_before_date', 'po_reference'].forEach((k) => {
                                    if (revisionData[k]) payload[k] = revisionData[k];
                                });
                                if (Object.keys(payload).length === 1) {
                                    toast.error('Change at least one field before saving the revision');
                                    return;
                                }
                                setRevising(true);
                                try {
                                    const { data: updated } = await ordersApi.reviseSupplierConfirmation(order.id, payload, variant);
                                    setConfirmation(updated);
                                    toast.success(`Confirmation revised → V${updated.current_version}`);
                                    setShowReviseDialog(false);
                                    setRevisionData({ quantity_confirmed: '', unit_price: '', total_amount: '', currency: '', estimated_ship_date: '', estimated_arrival_date: '', not_arrive_before_date: '', po_reference: '', reason: '' });
                                    if (typeof onUpdate === 'function') onUpdate();
                                } catch (err) {
                                    toast.error(err?.response?.data?.detail || 'Failed to revise confirmation');
                                } finally {
                                    setRevising(false);
                                }
                            }}
                            data-testid="revise-save-btn"
                        >
                            {revising ? 'Saving…' : `Save as V${(confirmation?.current_version || 1) + 1}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Undo Dispatch dialog — admin recovery. Explicitly states
                what will change so an admin can decide with confidence
                before rolling back a dispatched-and-sent order. */}
            <Dialog open={showUndoPODialog} onOpenChange={setShowUndoPODialog}>
                <DialogContent className="sm:max-w-lg" data-testid="undo-po-sent-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-amber-600" />
                            {t('supplier.undo_po_title', 'Revert Purchase Order send')}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <p className="text-slate-700">
                            {t('supplier.undo_po_body',
                               'This restores the Production Order workflow to its exact pre-send state:')}
                        </p>
                        <ul className="list-disc pl-5 text-xs text-slate-600 space-y-1">
                            <li>{t('supplier.undo_po_item_lock', 'Clears the PO Sent lock')}</li>
                            <li>{t('supplier.undo_po_item_oc', 'Reopens the Order Confirmation step for review')}</li>
                            <li>{t('supplier.undo_po_item_status', 'Reverts the order status (In Progress → Submitted on production, TP sidebar back to Required)')}</li>
                            <li>{t('supplier.undo_po_item_data', 'Preserves all entered data (LEOS#, quantities, dates) so you can re-review and re-send')}</li>
                        </ul>
                        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                            {t('supplier.undo_po_email_warning',
                               'The original PO email has already been sent and cannot be recalled — the supplier is responsible for any out-of-band notification.')}
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="undo-po-reason" className="text-xs">
                                {t('supplier.undo_po_reason', 'Reason')}
                                <span className="text-slate-400 ml-1">({t('common.optional', 'optional')})</span>
                            </Label>
                            <Textarea
                                id="undo-po-reason"
                                rows={2}
                                value={undoPOReason}
                                onChange={(e) => setUndoPOReason(e.target.value)}
                                placeholder={t('supplier.undo_po_reason_hint',
                                               'e.g. "Sent in error — LEOS# was wrong"')}
                                className="text-sm"
                                data-testid="undo-po-reason"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowUndoPODialog(false)}
                            disabled={isUndoingPO}
                            data-testid="undo-po-cancel"
                        >
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button
                            onClick={handleUndoPOSent}
                            disabled={isUndoingPO}
                            className="bg-amber-600 hover:bg-amber-700"
                            data-testid="undo-po-confirm"
                        >
                            {isUndoingPO && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {t('supplier.undo_po_confirm', 'Revert send')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Undo Dispatch dialog — admin recovery. Explicitly states
                what will change so an admin can decide with confidence
                before rolling back a dispatched-and-sent order. */}
            <Dialog open={showUndoDispatchDialog} onOpenChange={setShowUndoDispatchDialog}>
                <DialogContent className="sm:max-w-lg" data-testid="undo-dispatch-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                            Undo dispatch
                        </DialogTitle>
                        <DialogDescription>
                            This will reopen the Dispatch step for editing. All entered
                            data (delivery note number, quantity, dates, destination, PDF)
                            is preserved in the audit history — nothing is deleted.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3 py-2">
                        <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs space-y-1.5">
                            <div className="font-medium text-slate-700 mb-1">The following will be restored:</div>
                            <div className="flex items-center gap-2 text-slate-600">
                                <span className="w-1 h-1 bg-slate-400 rounded-full"></span>
                                <span>Supplier confirmation: <b>Approved & Dispatched</b> → <b>Approved (Dispatch reopened)</b></span>
                            </div>
                            {isProductionVariant ? (
                                <>
                                    <div className="flex items-center gap-2 text-slate-600">
                                        <span className="w-1 h-1 bg-slate-400 rounded-full"></span>
                                        <span>Order status: <b>Shipping</b> → <b>In Progress</b> (if currently Shipping)</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-slate-600">
                                        <span className="w-1 h-1 bg-slate-400 rounded-full"></span>
                                        <span>Delivery date auto-set from dispatch: <b>cleared</b></span>
                                    </div>
                                    <div className="flex items-center gap-2 text-slate-600">
                                        <span className="w-1 h-1 bg-slate-400 rounded-full"></span>
                                        <span>Invoice: <b>Ready to Book</b> → <b>Draft</b> (Book-to-e-conomic hidden; sales data preserved)</span>
                                    </div>
                                </>
                            ) : (
                                <div className="flex items-center gap-2 text-slate-600">
                                    <span className="w-1 h-1 bg-slate-400 rounded-full"></span>
                                    <span>Test Press sidebar: <b>Dispatched</b> → <b>Confirmed</b></span>
                                </div>
                            )}
                            <div className="border-t border-slate-200 mt-2 pt-2 text-slate-500">
                                Purchase Order and Order Confirmation state are NOT touched.
                                {isProductionVariant && ' Invoice quantity, prices and lines are preserved — only the readiness to invoice is reset. If the invoice is already in e-conomic (draft or booked) it is left alone.'}
                            </div>
                        </div>

                        {isDeliveryNoticeSent && (
                            <div className="rounded-md bg-amber-50 border border-amber-300 p-3 text-xs">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                                    <div className="space-y-2">
                                        <div className="text-amber-900 font-medium">
                                            The previous customer notification cannot be unsent.
                                        </div>
                                        <div className="text-amber-800">
                                            A delivery notice email was sent to the customer on{' '}
                                            {formatDateTime(confirmation?.delivery_notice_sent_at)}.
                                            Undoing the dispatch will NOT recall that email.
                                        </div>
                                        <label className="flex items-start gap-2 cursor-pointer">
                                            <Checkbox
                                                checked={undoAckSent}
                                                onCheckedChange={(v) => setUndoAckSent(!!v)}
                                                data-testid="undo-dispatch-ack-notification"
                                            />
                                            <span className="text-amber-900">
                                                I understand the customer email cannot be recalled.
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div>
                            <Label htmlFor="undo-reason" className="text-xs">
                                Reason (optional — added to audit log)
                            </Label>
                            <Textarea
                                id="undo-reason"
                                value={undoReason}
                                onChange={(e) => setUndoReason(e.target.value)}
                                placeholder="e.g. Approved on wrong order — actually belongs to #000142"
                                className="mt-1 text-xs"
                                rows={2}
                                data-testid="undo-dispatch-reason"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowUndoDispatchDialog(false)}
                            disabled={isSaving}
                            data-testid="undo-dispatch-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleUndoDispatch}
                            disabled={isSaving || (isDeliveryNoticeSent && !undoAckSent)}
                            className="bg-amber-600 hover:bg-amber-700"
                            data-testid="undo-dispatch-confirm"
                        >
                            {isSaving ? 'Undoing…' : 'Undo dispatch'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>



            {/* Version History dialog — shows every past confirmation snapshot */}
            <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
                <DialogContent className="sm:max-w-3xl max-h-[80vh] flex flex-col" data-testid="confirmation-history-dialog">
                    <DialogHeader>
                        <DialogTitle>Confirmation version history</DialogTitle>
                        <DialogDescription>
                            Every version of this supplier confirmation. The most recent version is currently active for dispatch and invoicing.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto space-y-3 -mx-6 px-6">
                        {[...(confirmation?.versions || []), {
                            version: confirmation?.current_version || 1,
                            quantity_confirmed: confirmation?.quantity_confirmed,
                            unit_price: confirmation?.unit_price,
                            total_amount: confirmation?.total_amount,
                            currency: confirmation?.currency,
                            estimated_ship_date: confirmation?.estimated_ship_date,
                            estimated_arrival_date: confirmation?.estimated_arrival_date,
                            not_arrive_before_date: confirmation?.not_arrive_before_date,
                            po_reference: confirmation?.po_reference,
                            source_approved_at: confirmation?.approved_at,
                            source_approved_by_name: confirmation?.approved_by_name,
                            revised_by_name: confirmation?.revised_by_name,
                            revised_reason: confirmation?.revised_reason,
                            revised_at: confirmation?.revised_at,
                            _is_current: true,
                        }].sort((a, b) => (a.version || 0) - (b.version || 0)).map((v) => (
                            <div key={v.version} className={`border rounded-md p-3 text-xs ${v._is_current ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`} data-testid={`version-row-v${v.version}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <span className={`px-1.5 py-0.5 rounded font-semibold ${v._is_current ? 'bg-emerald-600 text-white' : 'bg-slate-400 text-white'}`}>V{v.version}</span>
                                        {v._is_current ? (
                                            <span className="text-emerald-800 font-medium">Active</span>
                                        ) : (
                                            <span className="text-slate-500">Superseded {v.archived_at ? new Date(v.archived_at).toLocaleString('da-DK') : ''}</span>
                                        )}
                                    </div>
                                    <div className="text-slate-500 text-[11px]">
                                        {v.revised_at ? `Revised ${new Date(v.revised_at).toLocaleString('da-DK')}` : v.source_approved_at ? `Approved ${new Date(v.source_approved_at).toLocaleString('da-DK')}` : ''}
                                        {(v.revised_by_name || v.source_approved_by_name) && (
                                            <span className="ml-2">by <span className="font-medium text-slate-700">{v.revised_by_name || v.source_approved_by_name}</span></span>
                                        )}
                                    </div>
                                </div>
                                <div className="grid grid-cols-4 gap-2 text-slate-600">
                                    <div><div className="text-[10px] text-slate-400 uppercase">Qty</div>{v.quantity_confirmed ?? '—'}</div>
                                    <div><div className="text-[10px] text-slate-400 uppercase">Total</div>{v.total_amount != null ? `${v.total_amount} ${v.currency || ''}` : '—'}</div>
                                    <div><div className="text-[10px] text-slate-400 uppercase">Ship</div>{v.estimated_ship_date || '—'}</div>
                                    <div><div className="text-[10px] text-slate-400 uppercase">Arrival</div>{v.estimated_arrival_date || '—'}</div>
                                    <div><div className="text-[10px] text-slate-400 uppercase">Not before</div>{v.not_arrive_before_date || '—'}</div>
                                    <div><div className="text-[10px] text-slate-400 uppercase">PO ref</div>{v.po_reference || '—'}</div>
                                    <div><div className="text-[10px] text-slate-400 uppercase">Unit price</div>{v.unit_price ?? '—'}</div>
                                </div>
                                {v.revised_reason && (
                                    <div className="mt-2 pt-2 border-t border-slate-200 text-slate-600">
                                        <span className="text-[10px] uppercase text-slate-400">Reason: </span>{v.revised_reason}
                                    </div>
                                )}
                            </div>
                        ))}
                        {(confirmation?.versions || []).length === 0 && (
                            <p className="text-xs text-slate-400 italic text-center py-4">Only the initial approved version exists. Any revisions will appear here.</p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" size="sm" onClick={() => setShowHistoryDialog(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );

    if (embedded) {
        return (
            <>
                {content}
                {dialogs}
            </>
        );
    }

    return (
        <Card className="border-slate-300 shadow-premium-sm">
            <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2">
                        <Package className="h-4 w-4 text-amber-600" />
                        {t('supplier.title', 'Supplier (Sony)')}
                    </CardTitle>
                    {confirmation?.status && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                            confirmation.status === 'Approved' ? 'bg-emerald-100 text-emerald-700' :
                            confirmation.status === 'Mismatch' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                        }`}>
                            {confirmation.status === 'Approved' && <CheckCircle className="h-3 w-3" />}
                            {confirmation.status === 'Mismatch' && <AlertTriangle className="h-3 w-3" />}
                            {confirmation.status !== 'Approved' && confirmation.status !== 'Mismatch' && <Clock className="h-3 w-3" />}
                            {confirmation.status}
                        </span>
                    )}
                </div>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0">
                {content}
            </CardContent>
            {dialogs}
        </Card>
    );
};



/**
 * SplitDispatchDestinations
 *
 * Renders one collapsible dispatch row per delivery_address on a split order.
 * Each row has its own delivery_note_number, pallet_number, shipped_quantity,
 * planned_arrival_date, and note. Saves go through the per-destination PATCH
 * endpoint so admin can dispatch each leg of the order independently.
 */
function SplitDispatchDestinations({ order, confirmation, isDispatchApproved, onUpdated, variant = 'production' }) {
    const destinations = order?.delivery_addresses || [];
    // Build a lookup of existing dispatch info by address_id for quick access
    const dispatchByAddress = new Map();
    (confirmation?.dispatch_destinations || []).forEach((d) => {
        if (d?.address_id) dispatchByAddress.set(d.address_id, d);
    });

    return (
        <div className="space-y-3" data-testid="split-dispatch-list">
            <div className="flex items-center justify-between text-xs text-slate-500">
                <div className="flex items-center gap-1.5">
                    <Package className="h-3.5 w-3.5" />
                    <span>Split delivery · {destinations.length} destinations</span>
                </div>
                <span className="text-[10px] text-slate-400">Each destination has its own dispatch info</span>
            </div>
            {destinations.map((addr, idx) => (
                <DestinationDispatchRow
                    key={addr.id || idx}
                    idx={idx}
                    address={addr}
                    existing={dispatchByAddress.get(addr.id) || {}}
                    orderId={order.id}
                    locked={isDispatchApproved}
                    onSaved={onUpdated}
                    variant={variant}
                />
            ))}
        </div>
    );
}

function DestinationDispatchRow({ idx, address, existing, orderId, locked, onSaved, variant = 'production' }) {
    const { t } = useTranslation();
    const [form, setForm] = useState({
        delivery_note_number: existing.delivery_note_number || '',
        pallet_number: existing.pallet_number || '',
        shipped_quantity: existing.shipped_quantity ?? '',
        planned_arrival_date: existing.planned_arrival_date || '',
        note: existing.note || '',
        // Prefilled from admin-editable ``destination`` on the row,
        // falling back to the auto-computed ``inbound_destination``
        // so the field is never empty when Inbound Hub has data.
        destination: existing.destination ?? existing.inbound_destination ?? '',
    });
    const [saving, setSaving] = useState(false);
    const matchedViaInbound = !!existing.matched_via_inbound_document_id;

    const handleSave = async () => {
        setSaving(true);
        try {
            const data = {
                delivery_note_number: form.delivery_note_number || null,
                pallet_number: form.pallet_number || null,
                shipped_quantity: form.shipped_quantity === '' ? null : parseInt(form.shipped_quantity, 10),
                planned_arrival_date: form.planned_arrival_date || null,
                note: form.note || null,
                destination: form.destination || null,
            };
            const res = await ordersApi.updateDispatchDestination(orderId, address.id, data, variant);
            toast.success(`Dispatch saved for ${address.label || `Delivery ${idx + 1}`}`);
            onSaved?.(res.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save dispatch info');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="rounded-lg border border-slate-200 bg-white" data-testid={`dispatch-destination-${idx}`}>
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-700">
                        {idx + 1}) {address.label || `Delivery ${idx + 1}`}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">Qty {address.quantity}</span>
                    {matchedViaInbound && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium" title="Auto-matched from inbound dispatch document">
                            Auto-matched
                        </span>
                    )}
                </div>
            </div>
            <div className="p-3 space-y-3">
                {/* Planned address panel — sourced from order.delivery_addresses[address_id].
                    Kept as the at-a-glance identifier for the matched destination. */}
                <div className="space-y-1" data-testid={`dispatch-destination-address-${idx}`}>
                    <Label className="text-[10px]">Planned address (from order)</Label>
                    <div className="text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2 whitespace-pre-line leading-snug text-slate-700">
                        {[
                            address.company_name,
                            address.attention_to && `Att: ${address.attention_to}`,
                            address.street,
                            [address.postal_code, address.city].filter(Boolean).join(' '),
                            address.country,
                        ].filter(Boolean).join('\n') || <span className="text-slate-400 italic">No destination address on file for this delivery</span>}
                    </div>
                </div>

                {/* Actual destination text received from the supplier's inbound
                    dispatch document — editable so an admin can adjust the block
                    the customer sees. Prefilled from ``inbound_destination`` on
                    first render. The audit copy in
                    ``dispatch_deliveries[].destination`` is never touched. */}
                <div className="space-y-1">
                    <Label className="text-[10px]">
                        {t('supplier.destination', 'Destination')}
                        {' '}
                        <span className="text-slate-400 font-normal">
                            — {existing.inbound_destination
                                ? 'prefilled from supplier, editable'
                                : 'not received yet'}
                        </span>
                    </Label>
                    <Textarea
                        value={form.destination}
                        onChange={(e) => setForm({ ...form, destination: e.target.value })}
                        className="text-sm min-h-[80px] resize-y"
                        rows={3}
                        placeholder={t('supplier.destination_placeholder', 'Warehouse address or delivery location (multi-line supported)')}
                        disabled={locked}
                        data-testid={`dispatch-destination-inbound-${idx}`}
                    />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                        <Label className="text-[10px]">Delivery Note Number</Label>
                        <Input
                            value={form.delivery_note_number}
                            onChange={(e) => setForm({ ...form, delivery_note_number: e.target.value })}
                            className="h-8 text-sm"
                            disabled={locked}
                            data-testid={`dispatch-dn-${idx}`}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-[10px]">DSV Tracking</Label>
                        <DSVTrackingDisplay
                            carrierTracking={existing?.carrier_tracking}
                            testIdSuffix={`dest-${idx}`}
                        />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                        <Label className="text-[10px]">Shipped Quantity</Label>
                        <Input
                            type="number"
                            value={form.shipped_quantity}
                            onChange={(e) => setForm({ ...form, shipped_quantity: e.target.value })}
                            className="h-8 text-sm"
                            disabled={locked}
                            data-testid={`dispatch-qty-${idx}`}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-[10px]">Planned Arrival Date</Label>
                        <Input
                            type="date"
                            value={form.planned_arrival_date}
                            onChange={(e) => setForm({ ...form, planned_arrival_date: e.target.value })}
                            className="h-8 text-sm"
                            disabled={locked}
                            data-testid={`dispatch-arrival-${idx}`}
                        />
                    </div>
                </div>
                <div className="space-y-1">
                    <Label className="text-[10px]">Note</Label>
                    <Input
                        value={form.note}
                        onChange={(e) => setForm({ ...form, note: e.target.value })}
                        className="h-8 text-sm"
                        disabled={locked}
                        placeholder="Optional dispatch note (e.g. carrier, tracking)"
                        data-testid={`dispatch-note-${idx}`}
                    />
                </div>
                {!locked && (
                    <div className="flex items-center justify-end">
                        <Button
                            size="sm"
                            onClick={handleSave}
                            disabled={saving}
                            className="h-7 text-xs"
                            data-testid={`dispatch-save-${idx}`}
                        >
                            {saving ? 'Saving…' : `Save destination ${idx + 1}`}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
